import {
  assertNever,
  createUpdateInfiniteDataRecipe,
  createUpdateMultipleItemsRecipe,
} from '@autone/utils';

import {
  type EditPreSeasonAggregationUpdate,
  type TransformedEditPreSeasonAggregationResponse,
  type TransformedGetPreSeasonAggregationsListResponse,
} from '../types';

type PreSeasonListResponse = TransformedGetPreSeasonAggregationsListResponse;

/**
 * Marks the edited rows' affected cells as fetching while the PATCH is in
 * flight. Which cells depends on what the update touches.
 */
export const editInFlightFlagsRecipe = (
  editedRowIds: Set<string>,
  update: EditPreSeasonAggregationUpdate,
) =>
  createUpdateInfiniteDataRecipe(
    createUpdateMultipleItemsRecipe<PreSeasonListResponse>({
      predicate: (item) => editedRowIds.has(item.id),
      updateRecipe: (item) => {
        switch (update.update_type) {
          case 'assortment_assort_all':
          case 'assortment_assort_none':
          case 'assortment_set_dates':
            item.is_fetching_assortment_dates = true;
            item.is_fetching_assortment_status = true;
            break;
          case 'initial_allocation_match_proposal':
            item.is_fetching_initial_allocation_recommendations = true;
            item.is_fetching_remaining_wh_stock = true;
            item.is_fetching_coverage_period = true;
            break;
          case 'initial_allocation_set_each':
          case 'initial_allocation_set_sum':
            item.is_fetching_remaining_wh_stock = true;
            item.is_fetching_coverage_period = true;
            break;
          case 'min_ia_set_each':
            item.is_fetching_min_ia = true;
            break;
          case 'target_ia_coverage_set_each':
            item.is_fetching_target_coverage = true;
            break;
          default:
            assertNever(update);
        }
      },
    }),
  );

export const adjustRequestsInFlightRecipe = (
  editedRowIds: Set<string>,
  delta: 1 | -1,
) =>
  createUpdateInfiniteDataRecipe(
    createUpdateMultipleItemsRecipe<PreSeasonListResponse>({
      predicate: (item) => editedRowIds.has(item.id),
      updateRecipe: (item) => {
        item.requests_in_flight += delta;
      },
    }),
  );

/**
 * Toggles the "after IA" warehouse-stock cell spinner on the given rows while
 * the post-edit refresh re-reads their metrics. The edited rows
 * (`excludeRowIds`) are skipped — their metrics come straight from the PATCH
 * response, so they are already fresh and never show the spinner.
 */
export const setRemainingWhStockFetchingRecipe = (
  rowIds: Set<string>,
  isFetching: boolean,
  excludeRowIds?: Set<string>,
) =>
  createUpdateInfiniteDataRecipe(
    createUpdateMultipleItemsRecipe<PreSeasonListResponse>({
      predicate: (item) => rowIds.has(item.id) && !excludeRowIds?.has(item.id),
      updateRecipe: (item) => {
        item.is_fetching_remaining_wh_stock = isFetching;
      },
    }),
  );

/** Merges the metrics from updated response into existing cache entries. */
export const mergePatchedMetricsRecipe = (
  patchedItems: TransformedEditPreSeasonAggregationResponse['items'],
) => {
  const patchedItemsMap = new Map(patchedItems.map((item) => [item.id, item]));
  return createUpdateInfiniteDataRecipe(
    createUpdateMultipleItemsRecipe<PreSeasonListResponse>({
      predicate: (item) => patchedItemsMap.has(item.id),
      updateRecipe: (item) => {
        item.metrics = patchedItemsMap.get(item.id)!.metrics;
      },
    }),
  );
};
